import React, { useMemo } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { useCurrentInfluencerProfile } from '../../hooks/useCurrentInfluencerProfile';
import { deriveCreatorFeedbackSidebarStats } from '../../lib/feedbackPosts/creatorSidebarStats';

function firstNicheLine(niche) {
  if (Array.isArray(niche) && niche.length > 0 && niche[0]) {
    const n = String(niche[0]).trim();
    return n ? `${n} creator` : '';
  }
  if (typeof niche === 'string' && niche.trim()) {
    const parts = niche.split(',').map((s) => s.trim()).filter(Boolean);
    if (parts[0]) return `${parts[0]} creator`;
  }
  return '';
}

/**
 * Logged-in creator mini card for Feedback (and similar) sidebar.
 * Not shown for guests.
 */
export default function LoggedInCreatorPreviewCard({ posts = [], onNavigate }) {
  const { user } = useAuth();
  const { influencerProfile, loading: profileLoading } = useCurrentInfluencerProfile();

  const stats = useMemo(
    () => deriveCreatorFeedbackSidebarStats(posts, user?.uid),
    [posts, user?.uid]
  );

  if (!user?.uid) return null;

  const displayName =
    influencerProfile?.displayName?.trim() ||
    influencerProfile?.identity?.displayName?.trim() ||
    user.displayName?.trim() ||
    'Creator';

  const handle =
    influencerProfile?.username?.trim() ||
    influencerProfile?.identity?.handle?.trim()?.replace(/^@/, '') ||
    '';

  const photo =
    influencerProfile?.profileImageUrl ||
    influencerProfile?.photoURL ||
    influencerProfile?.avatarUrl ||
    user.photoURL ||
    null;

  const identityLine =
    influencerProfile?.identity?.creatorTitle?.trim() ||
    influencerProfile?.creatorTitle?.trim() ||
    firstNicheLine(influencerProfile?.niche) ||
    firstNicheLine(influencerProfile?.identity?.subtitle) ||
    '';

  const location =
    influencerProfile?.identity?.location?.trim() ||
    influencerProfile?.location?.trim() ||
    '';

  const bannerUrl =
    influencerProfile?.bannerImageUrl?.trim() || null;

  const statItems = [
    { label: 'Your posts', value: stats.yourPosts },
    { label: 'Needs replies', value: stats.needsReplies },
    { label: 'Helped on', value: stats.helpedOnThreads },
  ];

  return (
    <aside
      className="w-full max-w-full min-w-0 overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm"
      aria-label="Your creator preview"
    >
      {/* Banner: top of card (z-0 so avatar can sit on top) */}
      <div className="relative z-0 h-14 w-full sm:h-16">
        {bannerUrl ? (
          <img
            src={bannerUrl}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            className="h-full w-full bg-gradient-to-br from-slate-100 to-slate-200/80"
            aria-hidden
          />
        )}
      </div>

      {/* Avatar: overlaps banner and content, higher z-index so it sits on top */}
      <div className="relative z-10 px-3">
        <div className="-mt-8 flex">
          <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-full border-2 border-white bg-slate-100 shadow-md ring-2 ring-slate-200/80">
            {photo ? (
              <img src={photo} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-lg font-semibold text-slate-500">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="px-3 pb-3 pt-0.5">
        <p className="truncate text-sm font-semibold text-slate-900">{displayName}</p>
        {handle ? (
          <p className="truncate text-xs text-slate-500">@{handle.replace(/^@/, '')}</p>
        ) : profileLoading ? (
          <p className="text-xs text-slate-400">Loading profile…</p>
        ) : null}

        {identityLine ? (
          <p className="mt-1.5 text-xs font-medium text-slate-700 leading-snug">{identityLine}</p>
        ) : null}
        {location ? (
          <p className="mt-0.5 text-xs text-slate-500">{location}</p>
        ) : null}

      <div className="mt-3 grid grid-cols-3 gap-1.5 border-t border-slate-100 pt-3">
        {statItems.map(({ label, value }) => (
          <div key={label} className="text-center">
            <p className="text-base font-semibold tabular-nums text-slate-900">{value}</p>
            <p className="mt-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-500 leading-tight">
              {label}
            </p>
          </div>
        ))}
      </div>
      <p className="mt-1.5 text-[9px] text-slate-400 leading-snug">
        Stats reflect recent activity on this feed.
      </p>

      {onNavigate ? (
        <button
          type="button"
          onClick={() => onNavigate('profile-edit')}
          className="mt-3 w-full rounded-lg border border-slate-200 bg-slate-50/80 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-ihTeal/30"
        >
          {handle ? 'View profile' : 'Complete your profile'}
        </button>
      ) : null}
      </div>
    </aside>
  );
}
